import fontkit from "@pdf-lib/fontkit";
import { LineCapStyle, PDFDocument, StandardFonts, degrees, rgb } from "pdf-lib";
import { loadPdfForEdit } from "../../../utils/loadPdfForEdit";
import { prettyMathText } from "./math-text";

const SUPER_TO_ASCII = {
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
  "⁴": "4",
  "⁵": "5",
  "⁶": "6",
  "⁷": "7",
  "⁸": "8",
  "⁹": "9",
  "⁻": "-",
  "⁺": "+",
};

const UNICODE_TO_ASCII = [
  [/√/g, "sqrt"],
  [/∛/g, "cbrt"],
  [/∜/g, "4thrt"],
  [/π/g, "pi"],
  [/∞/g, "inf"],
  [/×/g, "x"],
  [/÷/g, "/"],
  [/±/g, "+/-"],
  [/≤/g, "<="],
  [/≥/g, ">="],
  [/≠/g, "!="],
  [/≈/g, "~="],
  [/θ/g, "theta"],
  [/α/g, "alpha"],
  [/β/g, "beta"],
  [/γ/g, "gamma"],
  [/δ/g, "delta"],
  [/Δ/g, "Delta"],
  [/λ/g, "lambda"],
  [/μ/g, "mu"],
  [/σ/g, "sigma"],
  [/ω/g, "omega"],
  [/°/g, " deg"],
  [/½/g, "1/2"],
  [/⅓/g, "1/3"],
  [/⅔/g, "2/3"],
  [/¼/g, "1/4"],
  [/¾/g, "3/4"],
  [/⅕/g, "1/5"],
  [/⅖/g, "2/5"],
  [/⅗/g, "3/5"],
  [/⅘/g, "4/5"],
  [/⅙/g, "1/6"],
  [/⅚/g, "5/6"],
  [/⅐/g, "1/7"],
  [/⅛/g, "1/8"],
  [/⅜/g, "3/8"],
  [/⅝/g, "5/8"],
  [/⅞/g, "7/8"],
  [/⅑/g, "1/9"],
  [/⅒/g, "1/10"],
  [/⁄/g, "/"],
];

let unicodeFontBytesPromise = null;

function loadUnicodeFontBytes() {
  if (!unicodeFontBytesPromise) {
    const base = process.env.PUBLIC_URL || "";
    unicodeFontBytesPromise = fetch(`${base}/fonts/DejaVuSans.ttf`)
      .then((response) => {
        if (!response.ok) throw new Error("Unicode font missing");
        return response.arrayBuffer();
      })
      .then((buffer) => new Uint8Array(buffer))
      .catch((error) => {
        unicodeFontBytesPromise = null;
        throw error;
      });
  }
  return unicodeFontBytesPromise;
}

async function embedPdfFonts(pdfDoc) {
  try {
    const bytes = await loadUnicodeFontBytes();
    pdfDoc.registerFontkit(fontkit);
    const regular = await pdfDoc.embedFont(bytes, { subset: true });
    return {
      regular,
      bold: regular,
      italic: regular,
      allowUnicode: true,
    };
  } catch {
    return {
      regular: await pdfDoc.embedFont(StandardFonts.Helvetica),
      bold: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
      italic: await pdfDoc.embedFont(StandardFonts.HelveticaOblique),
      allowUnicode: false,
    };
  }
}

const C = {
  header: rgb(0.055, 0.239, 0.349),
  headerMuted: rgb(0.82, 0.9, 0.94),
  pageBg: rgb(0.97, 0.98, 0.99),
  fullBar: rgb(0.835, 0.941, 0.882),
  fullAccent: rgb(0.122, 0.635, 0.451),
  fullScore: rgb(0.078, 0.447, 0.416),
  partialBar: rgb(0.992, 0.925, 0.773),
  partialAccent: rgb(0.902, 0.494, 0.133),
  partialScore: rgb(0.827, 0.329, 0.0),
  failBar: rgb(0.98, 0.878, 0.878),
  failAccent: rgb(0.753, 0.224, 0.169),
  failScore: rgb(0.753, 0.224, 0.169),
  boxBlue: rgb(0.91, 0.937, 0.957),
  boxBlueBorder: rgb(0.173, 0.243, 0.314),
  boxRed: rgb(0.992, 0.929, 0.929),
  boxRedBorder: rgb(0.69, 0.133, 0.216),
  essayFill: rgb(0.99, 0.96, 0.92),
  essayBorder: rgb(0.62, 0.38, 0.12),
  reviseFill: rgb(0.91, 0.96, 0.93),
  reviseBorder: rgb(0.1, 0.45, 0.35),
  slateFill: rgb(0.94, 0.95, 0.96),
  slateBorder: rgb(0.29, 0.37, 0.45),
  ink: rgb(0.12, 0.18, 0.25),
  muted: rgb(0.45, 0.5, 0.55),
  white: rgb(1, 1, 1),
  watermark: rgb(0.78, 0.82, 0.86),
};

function preparePdfText(value, plain) {
  if (!plain) return prettyMathText(value);
  return String(value ?? "")
    .replace(/[–—]/g, "-")
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\u00a0/g, " ");
}

export function sanitizePdfText(value, { allowUnicode = false, plain = false } = {}) {
  let text = preparePdfText(value, plain);
  text = text.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");
  if (allowUnicode) {
    return text.replace(/[ \t]+\n/g, "\n").replace(/[ \t]{2,}/g, " ").trim();
  }
  if (!plain) {
    UNICODE_TO_ASCII.forEach(([pattern, replacement]) => {
      text = text.replace(pattern, replacement);
    });
    text = text.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺]/g, (ch) => SUPER_TO_ASCII[ch] || "");
    text = text.replace(/·/g, ".");
  }
  text = text.replace(/\s+/g, " ").trim();
  return text.replace(/[^\x20-\x7E]/g, "?");
}

function t(value, fonts) {
  return sanitizePdfText(value, { allowUnicode: Boolean(fonts?.allowUnicode) });
}

function asPdfBytes(pdfBytes) {
  return pdfBytes instanceof Uint8Array ? pdfBytes : new Uint8Array(pdfBytes);
}

export function getQuestionTone(question) {
  const awarded = Number(question?.marks_awarded);
  const max = Number(question?.max_marks);
  const status = String(question?.status || "").toLowerCase();
  if (Number.isFinite(awarded) && Number.isFinite(max) && max > 0) {
    if (awarded >= max) return "correct";
    if (awarded <= 0) return "incorrect";
    return "partial";
  }
  if (status === "correct") return "correct";
  if (status === "incorrect") return "incorrect";
  return "partial";
}

export function groupQuestionsByPage(questions) {
  const map = new Map();
  (questions || []).forEach((question) => {
    const page = Number(question?.page);
    if (!Number.isFinite(page) || page < 1) return;
    if (!map.has(page)) map.set(page, []);
    map.get(page).push(question);
  });
  return map;
}

export function resolveQuestionMark(question) {
  const mark = question?.mark;
  if (!mark || typeof mark !== "object" || Array.isArray(mark)) return null;
  const symbol = String(mark.symbol || "")
    .trim()
    .toLowerCase();
  if (symbol !== "tick" && symbol !== "cross") return null;
  const x = typeof mark.x === "number" && Number.isFinite(mark.x) ? mark.x : null;
  const y = typeof mark.y === "number" && Number.isFinite(mark.y) ? mark.y : null;
  const pageNum = Number(mark.page);
  const page = Number.isFinite(pageNum) && pageNum >= 1 ? Math.trunc(pageNum) : null;
  return {
    symbol,
    page,
    x,
    y,
    positioned: x != null && y != null && page != null,
  };
}

const FALLBACK_MARK_X = 0.93;

function questionPageNumber(question) {
  const page = Number(question?.page);
  if (Number.isFinite(page) && page >= 1) return Math.trunc(page);
  const markPage = Number(question?.mark?.page);
  if (Number.isFinite(markPage) && markPage >= 1) return Math.trunc(markPage);
  return null;
}

export function questionsWithFallbackMarks(questions) {
  const byPage = new Map();
  (questions || []).forEach((question, index) => {
    const page = questionPageNumber(question);
    if (!page) return;
    if (!byPage.has(page)) byPage.set(page, []);
    byPage.get(page).push(index);
  });

  const next = (questions || []).slice();
  byPage.forEach((indexes, page) => {
    const count = indexes.length;
    indexes.forEach((listIndex, bandIndex) => {
      const question = next[listIndex];
      const mark = resolveQuestionMark(question);
      if (!mark || mark.positioned) return;
      next[listIndex] = {
        ...question,
        mark: {
          ...(question.mark && typeof question.mark === "object" ? question.mark : {}),
          symbol: mark.symbol,
          page,
          x: FALLBACK_MARK_X,
          y: (bandIndex + 0.5) / count,
        },
      };
    });
  });
  return next;
}

function pageVisualSpace(page) {
  const rotation = ((page.getRotation().angle % 360) + 360) % 360;
  const box = page.getCropBox();
  const swapped = rotation === 90 || rotation === 270;
  return {
    rotation,
    box,
    width: swapped ? box.height : box.width,
    height: swapped ? box.width : box.height,
  };
}

function visualPointToPdf(space, vx, vy) {
  const { rotation, box } = space;
  const { x, y, width, height } = box;
  if (rotation === 90) return { x: x + vy, y: y + vx };
  if (rotation === 180) return { x: x + width - vx, y: y + vy };
  if (rotation === 270) return { x: x + width - vy, y: y + height - vx };
  return { x: x + vx, y: y + height - vy };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function markSegments(symbol, cx, cy, size) {
  if (symbol === "cross") {
    const arm = size * 0.34;
    return [
      [
        { x: cx - arm, y: cy - arm },
        { x: cx + arm, y: cy + arm },
      ],
      [
        { x: cx + arm, y: cy - arm },
        { x: cx - arm, y: cy + arm },
      ],
    ];
  }
  return [
    [
      { x: cx - size * 0.36, y: cy + size * 0.02 },
      { x: cx - size * 0.08, y: cy + size * 0.32 },
    ],
    [
      { x: cx - size * 0.08, y: cy + size * 0.32 },
      { x: cx + size * 0.4, y: cy - size * 0.34 },
    ],
  ];
}

function drawMarkGlyph(page, space, symbol, vx, vy, size) {
  const half = size / 2;
  const cx = clamp(vx, half, Math.max(half, space.width - half));
  const cy = clamp(vy, half, Math.max(half, space.height - half));
  const color = symbol === "cross" ? C.failAccent : C.fullScore;
  const thickness = Math.max(1.8, size * 0.16);
  const paint = (stroke, strokeWidth) => {
    markSegments(symbol, cx, cy, size).forEach(([start, end]) => {
      page.drawLine({
        start: visualPointToPdf(space, start.x, start.y),
        end: visualPointToPdf(space, end.x, end.y),
        thickness: strokeWidth,
        color: stroke,
        lineCap: LineCapStyle.Round,
      });
    });
  };
  paint(C.white, thickness + 2.2);
  paint(color, thickness);
}

function questionForFeedbackSheet(question, pageCount) {
  const mark = resolveQuestionMark(question);
  if (!mark?.positioned || mark.page <= pageCount) return question;
  return {
    ...question,
    mark: { ...question.mark, x: null, y: null },
  };
}

function drawPositionedQuestionMarks(doc, questions) {
  const pageCount = doc.getPageCount();
  let drawn = 0;
  (questions || []).forEach((question) => {
    const mark = resolveQuestionMark(question);
    if (!mark?.positioned || mark.page > pageCount) return;
    const page = doc.getPage(mark.page - 1);
    const space = pageVisualSpace(page);
    const size = clamp(Math.min(space.width, space.height) * 0.05, 16, 42);
    drawMarkGlyph(page, space, mark.symbol, mark.x * space.width, mark.y * space.height, size);
    drawn += 1;
  });
  return drawn;
}

function cleanText(value) {
  return String(value ?? "").trim();
}

export function formatMistakeKind(kind) {
  const raw = cleanText(kind).replace(/[_-]+/g, " ");
  if (!raw) return "";
  return raw.replace(/\b[a-z]/g, (char) => char.toUpperCase());
}

export function mistakeCorrectionText(annotation) {
  const correction = annotation?.correction;
  if (correction != null && cleanText(correction)) return cleanText(correction);
  return cleanText(annotation?.comment);
}

export function writingReportFields(question) {
  if (!question || typeof question !== "object") return {};
  const fields = {};

  const components = (Array.isArray(question.components) ? question.components : []).filter(
    (item) => item && cleanText(item.name)
  );
  if (components.length) fields.components = components;

  const annotations = (Array.isArray(question.annotations) ? question.annotations : []).filter(
    (item) =>
      item &&
      (cleanText(item.quote) ||
        cleanText(item.kind) ||
        cleanText(item.correction) ||
        cleanText(item.comment))
  );
  if (annotations.length) fields.annotations = annotations;

  const areas = (Array.isArray(question.areas_of_improvement) ? question.areas_of_improvement : [])
    .map((item) => cleanText(item))
    .filter(Boolean);
  if (areas.length) fields.areas_of_improvement = areas;

  const content = cleanText(question.content_feedback);
  if (content) fields.content_feedback = content;

  const reason = cleanText(question.grade_reason);
  if (reason) fields.grade_reason = reason;

  const revised = cleanText(question.revised_answer);
  if (revised) fields.revised_answer = revised;

  if (
    question.word_count != null &&
    question.word_count !== "" &&
    Number.isFinite(Number(question.word_count))
  ) {
    fields.word_count = Number(question.word_count);
  }

  return fields;
}

function wrapText(text, font, size, maxWidth, allowUnicode = false, plain = false) {
  const clean = sanitizePdfText(text, { allowUnicode, plain });
  if (!clean) return [];
  const words = clean.split(/\s+/);
  const lines = [];
  let current = "";

  const pushLongWord = (word) => {
    let chunk = "";
    for (const ch of word) {
      const next = chunk + ch;
      if (font.widthOfTextAtSize(next, size) <= maxWidth) {
        chunk = next;
      } else {
        if (chunk) lines.push(chunk);
        chunk = ch;
      }
    }
    if (chunk) current = chunk;
  };

  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      if (font.widthOfTextAtSize(word, size) <= maxWidth) {
        current = word;
      } else {
        current = "";
        pushLongWord(word);
      }
    }
  }
  if (current) lines.push(current);
  return lines;
}

function drawHeader(page, { width, height, pageNum, questionIds, meta, isContinuation, fonts }) {
  const headerH = 72;
  page.drawRectangle({
    x: 0,
    y: height - headerH,
    width,
    height: headerH,
    color: C.header,
  });

  const title = isContinuation
    ? `Feedback - Page ${pageNum} (cont.)`
    : `Feedback - Page ${pageNum}`;
  page.drawText(t(title, fonts), {
    x: 28,
    y: height - 34,
    size: 20,
    font: fonts.bold,
    color: C.white,
  });

  const ids = t(questionIds.join(" . "), fonts);
  if (ids) {
    page.drawText(ids, {
      x: 28,
      y: height - 54,
      size: 10,
      font: fonts.regular,
      color: C.headerMuted,
    });
  }

  const right = t(meta?.contextLabel || "", fonts);
  if (right) {
    const rightWidth = fonts.regular.widthOfTextAtSize(right, 9);
    page.drawText(right, {
      x: Math.max(28, width - 28 - rightWidth),
      y: height - 54,
      size: 9,
      font: fonts.regular,
      color: C.headerMuted,
    });
  }
}

function drawWatermark(page, text, fonts, width, height) {
  const label = t(text, fonts);
  if (!label) return;
  page.drawText(label.toUpperCase(), {
    x: width * 0.12,
    y: height * 0.28,
    size: Math.min(42, width / 10),
    font: fonts.bold,
    color: C.watermark,
    rotate: degrees(32),
    opacity: 0.22,
  });
}

function createCursor(out, { width, height, fonts, pageNum, questionIds, meta }) {
  let page = null;
  let y = 0;
  const margin = 32;
  const contentX = 28;
  const contentWidth = width - 56;

  const startPage = () => {
    const isContinuation = Boolean(page);
    page = out.addPage([width, height]);
    page.drawRectangle({ x: 0, y: 0, width, height, color: C.pageBg });
    drawHeader(page, {
      width,
      height,
      pageNum,
      questionIds,
      meta,
      isContinuation,
      fonts,
    });
    drawWatermark(page, meta?.watermark, fonts, width, height);
    y = height - 92;
  };

  const ensure = (needed) => {
    if (!page || y - needed < margin) startPage();
  };

  return {
    contentX,
    contentWidth,
    ensure,
    get y() {
      return y;
    },
    set y(next) {
      y = next;
    },
    get page() {
      return page;
    },
  };
}

function toneColors(tone) {
  if (tone === "correct") {
    return { bar: C.fullBar, accent: C.fullAccent, score: C.fullScore };
  }
  if (tone === "incorrect") {
    return { bar: C.failBar, accent: C.failAccent, score: C.failScore };
  }
  return { bar: C.partialBar, accent: C.partialAccent, score: C.partialScore };
}

function drawLabeledBox(cursor, fonts, { title, body, fill, border }) {
  const titleSize = 8;
  const bodySize = 10;
  const pad = 8;
  const innerW = cursor.contentWidth - pad * 2 - 4;
  const lines = wrapText(body, fonts.regular, bodySize, innerW, fonts.allowUnicode);
  const contentH = Math.max(13, lines.length * 13);
  const boxH = pad + 12 + contentH + pad;
  cursor.ensure(boxH + 8);

  cursor.page.drawRectangle({
    x: cursor.contentX,
    y: cursor.y - boxH,
    width: cursor.contentWidth,
    height: boxH,
    color: fill,
  });
  cursor.page.drawRectangle({
    x: cursor.contentX,
    y: cursor.y - boxH,
    width: 5,
    height: boxH,
    color: border,
  });
  cursor.page.drawText(t(title, fonts), {
    x: cursor.contentX + pad + 4,
    y: cursor.y - pad - 8,
    size: titleSize,
    font: fonts.bold,
    color: border,
  });
  lines.forEach((line, index) => {
    cursor.page.drawText(line, {
      x: cursor.contentX + pad + 4,
      y: cursor.y - pad - 24 - index * 13,
      size: bodySize,
      font: fonts.regular,
      color: C.ink,
    });
  });
  cursor.y -= boxH + 8;
}

function plainText(value, fonts) {
  return sanitizePdfText(value, { allowUnicode: Boolean(fonts?.allowUnicode), plain: true });
}

function writingRowHeight(row) {
  if (row.kind === "gap") return 6;
  if (row.kind === "label") return 12;
  return 13;
}

function writingChunkHeight(rows, withTitle) {
  let height = 8;
  if (withTitle) height += 16;
  rows.forEach((row) => {
    height += writingRowHeight(row);
  });
  return height + 8;
}

function buildWritingRows(fonts, segments, innerWidth) {
  const rows = [];
  (segments || []).forEach((segment) => {
    const label = plainText(segment.label || "", fonts);
    const lines = wrapText(segment.body || "", fonts.regular, 10, innerWidth, fonts.allowUnicode, true);
    if (!label && !lines.length) return;
    if (rows.length) rows.push({ kind: "gap" });
    if (label) rows.push({ kind: "label", text: label });
    lines.forEach((line) => rows.push({ kind: "body", text: line }));
  });
  return rows;
}

function drawWritingCard(cursor, fonts, { title, segments, fill, border }) {
  const pad = 8;
  const innerWidth = cursor.contentWidth - pad * 2 - 4;
  const rows = buildWritingRows(fonts, segments, innerWidth);
  const titleText = plainText(title || "", fonts);
  if (!titleText && !rows.length) return;

  let index = 0;
  let titlePending = Boolean(titleText);

  while (titlePending || index < rows.length) {
    if (!cursor.page || cursor.y - 32 < 48) cursor.ensure(80);
    const space = Math.max(48, cursor.y - 32);
    const titleBlock = titlePending ? 16 : 0;
    let used = 8 + titleBlock;
    let end = index;
    while (end < rows.length) {
      const next = used + writingRowHeight(rows[end]) + 8;
      if (next > space && end > index) break;
      used += writingRowHeight(rows[end]);
      end += 1;
      if (used + 8 >= space) break;
    }
    if (end === index && index < rows.length) end = index + 1;

    const slice = rows.slice(index, end);
    const boxH = writingChunkHeight(slice, titlePending);
    const top = cursor.y;
    cursor.page.drawRectangle({
      x: cursor.contentX,
      y: top - boxH,
      width: cursor.contentWidth,
      height: boxH,
      color: fill,
    });
    cursor.page.drawRectangle({
      x: cursor.contentX,
      y: top - boxH,
      width: 5,
      height: boxH,
      color: border,
    });

    let y = top - pad;
    if (titlePending) {
      cursor.page.drawText(titleText, {
        x: cursor.contentX + pad + 4,
        y: y - 8,
        size: 8,
        font: fonts.bold,
        color: border,
      });
      y -= 16;
      titlePending = false;
    }
    slice.forEach((row) => {
      if (row.kind === "gap") {
        y -= 6;
        return;
      }
      if (row.kind === "label") {
        cursor.page.drawText(row.text, {
          x: cursor.contentX + pad + 4,
          y: y - 8,
          size: 8,
          font: fonts.bold,
          color: border,
        });
        y -= 12;
        return;
      }
      cursor.page.drawText(row.text, {
        x: cursor.contentX + pad + 4,
        y: y - 10,
        size: 10,
        font: fonts.regular,
        color: C.ink,
      });
      y -= 13;
    });

    cursor.y = top - boxH - 8;
    const previous = index;
    index = end;
    if (!titlePending && index >= rows.length) break;
    if (index === previous && !titlePending) break;
  }
}

function drawWritingReport(cursor, fonts, question) {
  const fields = writingReportFields(question);
  if (!Object.keys(fields).length) return;

  if (fields.components) {
    const separator = fonts.allowUnicode ? "  ·  " : "  |  ";
    drawWritingCard(cursor, fonts, {
      title: "MARK SPLIT",
      fill: C.boxBlue,
      border: C.boxBlueBorder,
      segments: [
        {
          body: fields.components
            .map(
              (item) =>
                `${cleanText(item.name)} ${formatScoreNumber(item.marks_awarded)}/${formatScoreNumber(item.max_marks)}`
            )
            .join(separator),
        },
      ],
    });
  }

  if (fields.word_count != null) {
    const label = plainText(`Word count: ${formatScoreNumber(fields.word_count)}`, fonts);
    cursor.ensure(18);
    cursor.page.drawText(label, {
      x: cursor.contentX + 6,
      y: cursor.y - 12,
      size: 10,
      font: fonts.bold,
      color: C.ink,
    });
    cursor.y -= 18;
  }

  (fields.annotations || []).forEach((annotation) => {
    const segments = [];
    if (cleanText(annotation.quote)) segments.push({ body: annotation.quote });
    const kind = formatMistakeKind(annotation.kind);
    if (kind) segments.push({ label: "TYPE OF MISTAKE", body: kind });
    const corrected = mistakeCorrectionText(annotation);
    if (corrected) segments.push({ label: "CORRECTED SENTENCE", body: corrected });
    drawWritingCard(cursor, fonts, {
      title: "MISTAKE",
      segments,
      fill: C.essayFill,
      border: C.essayBorder,
    });
  });

  if (fields.areas_of_improvement) {
    const bullet = fonts.allowUnicode ? "• " : "- ";
    drawWritingCard(cursor, fonts, {
      title: "AREAS FOR IMPROVEMENT",
      fill: C.slateFill,
      border: C.slateBorder,
      segments: fields.areas_of_improvement.map((area) => ({ body: `${bullet}${area}` })),
    });
  }

  if (fields.content_feedback) {
    drawWritingCard(cursor, fonts, {
      title: "FEEDBACK ON CONTENT",
      fill: C.boxBlue,
      border: C.boxBlueBorder,
      segments: [{ body: fields.content_feedback }],
    });
  }

  if (fields.grade_reason) {
    drawWritingCard(cursor, fonts, {
      title: "GRADE REASON",
      fill: C.partialBar,
      border: C.partialAccent,
      segments: [{ body: fields.grade_reason }],
    });
  }

  if (fields.revised_answer) {
    drawWritingCard(cursor, fonts, {
      title: "REVISED VERSION",
      fill: C.reviseFill,
      border: C.reviseBorder,
      segments: [{ body: fields.revised_answer }],
    });
  }
}

function drawQuestionCard(cursor, fonts, question) {
  const tone = getQuestionTone(question);
  const colors = toneColors(tone);
  const id = t(question.id || "Question", fonts);
  const awarded = Number.isFinite(Number(question.marks_awarded))
    ? String(question.marks_awarded)
    : "-";
  const max = Number.isFinite(Number(question.max_marks)) ? String(question.max_marks) : "-";
  const score = `${awarded}/${max}`;
  const barH = 26;

  cursor.ensure(barH + 48);
  const barTop = cursor.y;
  cursor.page.drawRectangle({
    x: cursor.contentX,
    y: cursor.y - barH,
    width: cursor.contentWidth,
    height: barH,
    color: colors.bar,
  });
  cursor.page.drawRectangle({
    x: cursor.contentX,
    y: cursor.y - barH,
    width: 5,
    height: barH,
    color: colors.accent,
  });
  cursor.page.drawText(id, {
    x: cursor.contentX + 14,
    y: cursor.y - 17,
    size: 12,
    font: fonts.bold,
    color: C.ink,
  });
  cursor.page.drawText("Written Answer", {
    x: cursor.contentX + 14 + fonts.bold.widthOfTextAtSize(id, 12) + 10,
    y: cursor.y - 16,
    size: 9,
    font: fonts.italic,
    color: C.muted,
  });
  const scoreWidth = fonts.bold.widthOfTextAtSize(score, 12);
  cursor.page.drawText(score, {
    x: cursor.contentX + cursor.contentWidth - 12 - scoreWidth,
    y: cursor.y - 17,
    size: 12,
    font: fonts.bold,
    color: colors.score,
  });
  const inlineMark = resolveQuestionMark(question);
  if (inlineMark && !inlineMark.positioned && !questionPageNumber(question)) {
    const space = pageVisualSpace(cursor.page);
    const vy = space.box.y + space.box.height - (barTop - barH / 2);
    drawMarkGlyph(cursor.page, space, inlineMark.symbol, 15, vy, 18);
  }
  cursor.y -= barH + 8;

  if (tone === "correct") {
    cursor.ensure(16);
    cursor.page.drawText("Full marks awarded", {
      x: cursor.contentX + 6,
      y: cursor.y - 10,
      size: 10,
      font: fonts.italic,
      color: colors.score,
    });
    cursor.y -= 18;
    if (question.feedback) {
      const lines = wrapText(
        question.feedback,
        fonts.regular,
        10,
        cursor.contentWidth - 6,
        fonts.allowUnicode
      );
      lines.forEach((line) => {
        cursor.ensure(13);
        cursor.page.drawText(line, {
          x: cursor.contentX + 6,
          y: cursor.y - 10,
          size: 10,
          font: fonts.regular,
          color: C.muted,
        });
        cursor.y -= 13;
      });
    }
  } else {
    if (question.correct_answer) {
      drawLabeledBox(cursor, fonts, {
        title: "CORRECT ANSWER",
        body: question.correct_answer,
        fill: C.boxBlue,
        border: C.boxBlueBorder,
      });
    }
    drawLabeledBox(cursor, fonts, {
      title: tone === "incorrect" ? "FEEDBACK" : "WHY MARKS WERE LOST",
      body: question.feedback || "Marks were lost on this question.",
      fill: C.boxRed,
      border: C.boxRedBorder,
    });
  }

  drawWritingReport(cursor, fonts, question);
  cursor.y -= 10;
}

function drawFeedbackSheets(out, options) {
  const questionIds = options.questions.map((q) => q.id).filter(Boolean);
  const cursor = createCursor(out, { ...options, questionIds });
  options.questions.forEach((question) => {
    drawQuestionCard(cursor, options.fonts, question);
  });
}

function formatScoreNumber(value) {
  if (!Number.isFinite(Number(value))) return "-";
  const n = Number(value);
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 100) / 100);
}

function hasCoverMeta(meta) {
  return Boolean(
    meta?.studentName || meta?.examName || meta?.awarded != null || meta?.max != null
  );
}

function drawCenteredLines(page, text, { y, size, font, color, width, maxWidth, lineGap = 6, allowUnicode = false }) {
  const lines = wrapText(text, font, size, maxWidth, allowUnicode);
  if (!lines.length) return 0;
  lines.forEach((line, index) => {
    const tw = font.widthOfTextAtSize(line, size);
    page.drawText(line, {
      x: (width - tw) / 2,
      y: y - index * (size + lineGap),
      size,
      font,
      color,
    });
  });
  return lines.length * (size + lineGap);
}

function drawCoverPage(out, { width, height, fonts, meta }) {
  const page = out.addPage([width, height]);
  page.drawRectangle({ x: 0, y: 0, width, height, color: C.pageBg });

  const headerH = Math.min(180, height * 0.24);
  page.drawRectangle({
    x: 0,
    y: height - headerH,
    width,
    height: headerH,
    color: C.header,
  });
  page.drawRectangle({
    x: 0,
    y: height - headerH - 6,
    width,
    height: 6,
    color: C.fullAccent,
  });

  const titleSize = Math.min(32, width / 14);
  drawCenteredLines(page, "Report", {
    y: height - headerH / 2 - titleSize * 0.35,
    size: titleSize,
    font: fonts.bold,
    color: C.white,
    width,
    maxWidth: width - 80,
    allowUnicode: fonts.allowUnicode,
  });

  const studentName = meta.studentName || meta.watermark || "Student";
  const examName = meta.examName || "";
  const awarded = Number(meta.awarded);
  const max = Number(meta.max);
  const scoreLabel = `${formatScoreNumber(awarded)} / ${formatScoreNumber(max)}`;
  const tone =
    Number.isFinite(awarded) && Number.isFinite(max) && max > 0
      ? awarded >= max
        ? "correct"
        : awarded <= 0
          ? "incorrect"
          : awarded / max >= 0.5
            ? "partial"
            : "incorrect"
      : "partial";
  const colors = toneColors(tone);
  const contentWidth = width - 96;
  let y = height - headerH - 64;

  const drawField = (label, value, valueSize) => {
    drawCenteredLines(page, label, {
      y,
      size: 10,
      font: fonts.bold,
      color: C.muted,
      width,
      maxWidth: contentWidth,
      allowUnicode: fonts.allowUnicode,
    });
    y -= 22;
    const used = drawCenteredLines(page, value, {
      y,
      size: valueSize,
      font: fonts.bold,
      color: C.ink,
      width,
      maxWidth: contentWidth,
      lineGap: 8,
      allowUnicode: fonts.allowUnicode,
    });
    y -= Math.max(used, valueSize) + 28;
  };

  drawField("STUDENT", studentName, 24);
  if (examName) {
    const used = drawCenteredLines(page, examName, {
      y,
      size: 16,
      font: fonts.bold,
      color: C.ink,
      width,
      maxWidth: contentWidth,
      lineGap: 8,
      allowUnicode: fonts.allowUnicode,
    });
    y -= Math.max(used, 16) + 28;
  }

  if (meta.hideScore) return;

  const cardW = Math.min(340, width - 96);
  const cardH = 96;
  const cardX = (width - cardW) / 2;
  const cardY = y - cardH;
  page.drawRectangle({
    x: cardX,
    y: cardY,
    width: cardW,
    height: cardH,
    color: colors.bar,
  });
  page.drawRectangle({
    x: cardX,
    y: cardY,
    width: 8,
    height: cardH,
    color: colors.accent,
  });

  const scoreTitle = "SCORE";
  page.drawText(scoreTitle, {
    x: cardX + (cardW - fonts.bold.widthOfTextAtSize(scoreTitle, 10)) / 2,
    y: cardY + cardH - 26,
    size: 10,
    font: fonts.bold,
    color: C.muted,
  });

  const cleanScore = t(scoreLabel, fonts);
  const scoreSize = 32;
  page.drawText(cleanScore, {
    x: cardX + (cardW - fonts.bold.widthOfTextAtSize(cleanScore, scoreSize)) / 2,
    y: cardY + 28,
    size: scoreSize,
    font: fonts.bold,
    color: colors.score,
  });
}

export async function insertAiFeedbackPages(pdfBytes, questions, meta = {}) {
  const placedQuestions = questionsWithFallbackMarks(questions);
  const grouped = groupQuestionsByPage(placedQuestions);
  const addCover = hasCoverMeta(meta);
  const wantsMarks = placedQuestions.some((question) => resolveQuestionMark(question)?.positioned);
  if (!grouped.size && !addCover && !wantsMarks) return asPdfBytes(pdfBytes);

  const doc = await loadPdfForEdit(pdfBytes);
  const drawnMarks = drawPositionedQuestionMarks(doc, placedQuestions);
  if (!grouped.size && !addCover) {
    if (!drawnMarks) return asPdfBytes(pdfBytes);
    return doc.save({ useObjectStreams: false });
  }

  const extras = await PDFDocument.create();
  const fonts = await embedPdfFonts(extras);
  const pageCount = doc.getPageCount();
  const { width, height } =
    pageCount > 0 ? doc.getPage(0).getSize() : { width: 595.28, height: 841.89 };

  const chunks = [];

  if (addCover) {
    const start = extras.getPageCount();
    drawCoverPage(extras, { width, height, fonts, meta });
    chunks.push({ kind: "cover", start, count: extras.getPageCount() - start });
  }

  for (let i = 0; i < pageCount; i++) {
    const pageQuestions = grouped.get(i + 1);
    if (!pageQuestions?.length) continue;
    const start = extras.getPageCount();
    const { width: pageWidth, height: pageHeight } = doc.getPage(i).getSize();
    drawFeedbackSheets(extras, {
      pageNum: i + 1,
      questions: pageQuestions.map((question) => questionForFeedbackSheet(question, pageCount)),
      width: pageWidth,
      height: pageHeight,
      fonts,
      meta,
    });
    chunks.push({
      kind: "after",
      afterIndex: i,
      start,
      count: extras.getPageCount() - start,
    });
  }

  for (let c = chunks.length - 1; c >= 0; c -= 1) {
    const chunk = chunks[c];
    const indices = [];
    for (let p = 0; p < chunk.count; p += 1) indices.push(chunk.start + p);
    const copied = await doc.copyPages(extras, indices);
    if (chunk.kind === "cover") {
      copied.forEach((page, idx) => doc.insertPage(idx, page));
    } else {
      copied.forEach((page, idx) => doc.insertPage(chunk.afterIndex + 1 + idx, page));
    }
  }

  return doc.save({ useObjectStreams: false });
}
